import _ from "lodash";
/**--internal-- */
import {
  generateRandomId,
  generateRandomNumber,
  getImageDimensions,
} from "Utils";

/**--relative-- */
import {
  ELEMENT_ORDER,
  DRAG_AND_DROP_CATEGORY,
  DRAG_AND_DROP_ITEM_TYPE,
} from "./DragAndDropConstant";
import { getLabelPlaceholder } from "../../LabellingField/Labelling/LabellingUtils";

export const isDraggable = type => {
  return _.isEqual(type, DRAG_AND_DROP_CATEGORY.DRAGGABLE);
};

export const isDropzone = type => {
  return _.isEqual(type, DRAG_AND_DROP_CATEGORY.DROPZONE);
};

/**
 *
 * @param {*} data
 * @param {*} mode
 * @param {*} drawMode
 * @param {*} allowInteraction
 * @returns mapping draggable state, according to user and mode
 */
export const sanitizeQuestionData = (
  data,
  { mode, drawMode, allowInteraction, viewSolution }
) => {
  let mappedData = [];
  if (viewSolution) {
    /**
     *
     * @param {*} item
     * @param {*} viewSolutionData
     * @description Updating content such that all dropzone shows correct draggable items inside it
     */
    const snapDraggableAnswerInDropzone = item => {
      const type = _.get(item, "type");
      const isRectangle = type === DRAG_AND_DROP_ITEM_TYPE.RECTANGLE;
      const isEllipse = type === DRAG_AND_DROP_ITEM_TYPE.ELLIPSE;

      let highestMidPoint = [0, 0];
      if (!isRectangle && !isEllipse) {
        // * for polygon dropzone get highest midpoint
        highestMidPoint = findHighestMidPointInPolygon(item);
      }

      // * next draggable element Y-axis coords
      let nextElementY = 4;
      if (isRectangle) {
        nextElementY += _.get(item, "y", 0);
      } else if (isEllipse) {
        nextElementY += _.get(item, "y", 0) - _.get(item, "radiusY", 0);
      } else {
        nextElementY += _.last(highestMidPoint);
      }

      // * loop through each correct elements and add it inside dropzone
      _.forEach(_.get(item, "correctElements"), elementId => {
        const originalDragItem = _.find(data, { id: elementId });
        const draggableItem = _.cloneDeep(originalDragItem);

        const type = _.get(draggableItem, "type");

        if (draggableItem) {
          _.set(draggableItem, "y", nextElementY);

          // * setting draggable element X-axis coords
          let nextElementX = 0;
          if (isRectangle) {
            nextElementX = _.get(item, "x") + 4;
          } else if (isEllipse) {
            nextElementX = _.get(item, "x", 0) - _.get(item, "radiusX", 0) / 2;
          } else {
            nextElementX = _.first(highestMidPoint) + 8;
          }
          _.set(draggableItem, "x", nextElementX);
        }

        if (_.get(draggableItem, "y")) {
          nextElementY = _.get(draggableItem, "y") + 4;

          const isText = type === DRAG_AND_DROP_ITEM_TYPE.TEXT;
          const isImage = type === DRAG_AND_DROP_ITEM_TYPE.IMAGE;

          if (isImage && _.has(draggableItem, "height")) {
            nextElementY += _.get(draggableItem, "height");
          } else if (isText) {
            nextElementY += _.get(draggableItem, "fontSize") * 2;
          }
        }

        mappedData.push(draggableItem);
      });
    };

    const groupedElementByCategory = _.groupBy(data, "category");
    const decorativeItems = _.get(
      groupedElementByCategory,
      DRAG_AND_DROP_CATEGORY.DECORATIVE,
      []
    );
    const dropzoneItems = _.get(
      groupedElementByCategory,
      DRAG_AND_DROP_CATEGORY.DROPZONE,
      []
    );

    mappedData = [...decorativeItems, ...dropzoneItems];
    _.forEach(dropzoneItems, item => snapDraggableAnswerInDropzone(item));
  } else {
    // * Updating draggable state according to mode of view
    mappedData = _.map(data, item => {
      const itemObject = { ...item };

      if (!allowInteraction) {
        // * When question is inactive in create mode, user is not allowed to interact
        itemObject.draggable = false;
        return itemObject;
      }

      if (_.get(drawMode, "isDrawing")) {
        itemObject.draggable = false;
      } else if (mode === "create") {
        itemObject.draggable = true;
      } else if (mode === "edit" || mode === "preview") {
        itemObject.draggable =
          item.category === DRAG_AND_DROP_CATEGORY.DRAGGABLE;
      }
      return itemObject;
    });
  }

  return stableOrderizeData(mappedData);
};

/**
 *
 * @param {*} category
 * @param {*} type
 * @param {*} item
 * @param {*} canvasId
 * @returns @returns Generate initial props for shape
 */
export const getInitPropsForNewShape = ({
  t = () => {},
  type = "",
  item = {},
  category = "",
  canvasId = "",
}) => {
  const commonProps = {
    type,
    category,
    x: generateRandomNumber(10, 100),
    y: generateRandomNumber(10, 100),
    id: generateRandomId(),
    draggable: true,
  };

  let shapeProps = {};

  if (type === DRAG_AND_DROP_ITEM_TYPE.TEXT) {
    // * For new text node calculating font size based on current canvas size.
    const width = _.get(document.getElementById(canvasId), "clientWidth", 778);
    const fontSize = width < 800 ? 16 : 16 * ((width || 778) / 778);
    shapeProps = {
      fontSize,
      text: t("worksheet:add_text_here"),
    };
  } else if (type === DRAG_AND_DROP_ITEM_TYPE.IMAGE) {
    shapeProps = {
      url: _.get(item, "url"),
      width: _.get(item, "width", 75),
      height: _.get(item, "height", 75),
    };
  } else if (_.includes([type, category], "LABEL")) {
    shapeProps = {
      arrows: [],
      text: getLabelPlaceholder(t, "create"),
    };
  }

  return _.merge({}, commonProps, shapeProps);
};

/**
 *
 * @param {*} data
 * @returns sort data in stable formate according to category
 */
export const stableOrderizeData = data => {
  const orderedElement = _.sortBy(data, (element, index) => [
    ELEMENT_ORDER.indexOf(_.get(element, "category")),
    index,
  ]);

  return orderedElement;
};

/**
 *
 * @param {*} dimensions
 * @returns { width, height } of image in aspect ratio
 */
export const getImageSizeWithAspectRatio = async url => {
  const dimensions = await getImageDimensions({ url, type: "IMAGE" });
  const minWidth = 100;
  const minHeight = 100;

  // * Calculate the aspect ratio
  const aspectRatio = dimensions.width / dimensions.height;

  // * Adjust the dimensions to ensure either width or height is at least 100 pixels
  let newWidth = dimensions.width;
  let newHeight = dimensions.height;

  // * Determine which dimension is smaller and adjust accordingly
  if (newWidth <= newHeight) {
    // * Set width to minimum and adjust height accordingly
    newWidth = minWidth;
    newHeight = newWidth / aspectRatio;
  } else {
    // * Set height to minimum and adjust width accordingly
    newHeight = minHeight;
    newWidth = newHeight * aspectRatio;
  }

  return { width: Math.round(newWidth), height: Math.round(newHeight) };
};

/**
 *
 * @param {*} data
 * @returns convert pixel to percentage
 */
export const convertToPercentage = (data, fieldId, mode) => {
  const canvas = document.getElementById(
    `konva-canvas-container-id-${fieldId}-${mode}`
  );
  if (!canvas) return null;

  const canvasHeight = canvas.clientHeight;
  const canvasWidth = canvas.clientWidth;

  // * Property converter
  const convert = (item, property, dimension) => {
    if (!_.has(item, property)) return;
    const convertedValue = (_.get(item, property) / dimension) * 100;
    _.set(item, property, convertedValue);
    return convertedValue;
  };

  return _.map(_.cloneDeep(data), item => {
    const element = _.cloneDeep(item);
    convert(element, "x", canvasWidth);
    convert(element, "width", canvasWidth);
    convert(element, "radiusX", canvasWidth);

    convert(element, "y", canvasHeight);
    convert(element, "height", canvasHeight);
    convert(element, "radiusY", canvasHeight);
    convert(element, "fontSize", canvasHeight);

    if (_.has(element, "points")) {
      element.points = _.map(element.points, (point, index) => {
        return (point / (index % 2 === 0 ? canvasWidth : canvasHeight)) * 100;
      });
    }

    return element;
  });
};

/**
 *
 * @param {*} data
 * @returns convert percentage to pixel
 */
export const convertToPixel = async (
  data,
  fieldId,
  mode,
  onConvert = () => {}
) => {
  const canvas = document.getElementById(
    `konva-canvas-container-id-${fieldId}-${mode}`
  );
  if (!canvas) {
    // * retry till canvas is not added into dom
    await new Promise(resolve => setTimeout(() => resolve(), 100));
    return convertToPixel(data, fieldId, mode, onConvert);
  }

  const canvasHeight = canvas.clientHeight;
  const canvasWidth = canvas.clientWidth;

  // * Property converter
  const convert = (item, property, dimension) => {
    if (!_.has(item, property)) return;
    _.set(
      item,
      property,
      (parseFloat(_.get(item, property)) / 100) * dimension
    );
  };

  const updatedData = _.map(data, item => {
    const element = _.cloneDeep(item);

    convert(element, "x", canvasWidth);
    convert(element, "width", canvasWidth);
    convert(element, "radiusX", canvasWidth);

    convert(element, "y", canvasHeight);
    convert(element, "height", canvasHeight);
    convert(element, "radiusY", canvasHeight);
    convert(element, "fontSize", canvasHeight);

    if (_.has(element, "points")) {
      element.points = _.map(element.points, (point, index) => {
        return (point / 100) * (index % 2 === 0 ? canvasWidth : canvasHeight);
      });
    }

    return element;
  });

  onConvert(updatedData);
};

/**
 *
 * @param {*} item of polygon
 * @returns [x, y] highest mid point
 */
export const findHighestMidPointInPolygon = item => {
  const points = _.get(item, "points", []);
  const x = _.get(item, "x", 0);
  const y = _.get(item, "y", 0);

  // * Split coordinates into segments
  const segments = _.chunk(points, 2);
  const mids = [];

  // * Get all possible mid points
  for (let i = 1; i < segments.length; i++) {
    const coord1 = segments[i - 1];
    const coord2 = segments[i];
    const midX = (_.first(coord1) + _.first(coord2)) / 2;
    const midY = (_.last(coord1) + _.last(coord2)) / 2;
    mids.push([midX, midY]);
  }

  // * find highest mid point
  const highestPoint = _.minBy(mids, item => _.last(item));

  return [_.first(highestPoint) + x, _.last(highestPoint) + y];
};

/**
 *
 * @description
 * if not fullscreen
 *    use 100% width
 * else
 *    Calculated max possible width and height considering header and side spaces,
 *    Next identify valid ratio height and width and selected one of them based on area
 *
 * @returns maximized possible width
 */
export const getDragAndDropCanvasWidth = isFullscreen => {
  if (!isFullscreen) return "100%";

  const maxPossibleHeight = window.innerHeight - 100; // * subtracting header and extra space
  const maxPossibleWidth = window.innerWidth * 0.9; // * 90% of window width will be actionable canvas

  const ratioHeight = (maxPossibleWidth * 9) / 16;
  const ratioWidth = (maxPossibleHeight * 16) / 9;

  const isValidRatioWidth = ratioWidth <= maxPossibleWidth;
  const isValidRatioHeight = ratioHeight <= maxPossibleHeight;

  let maxArea = 0;
  if (isValidRatioHeight) {
    maxArea = Math.max(maxArea, ratioHeight * maxPossibleWidth);
  }

  if (isValidRatioWidth && ratioWidth * maxPossibleHeight > maxArea) {
    return ratioWidth;
  } else {
    return maxPossibleWidth;
  }
};

/**
 *
 * @param {*} props
 * @returns Insert `dragAndDropAnswers` in elements
 */
export const injectCorrectElementsInData = value => {
  const dragAndDropAnswers = _.get(value, "dragAndDropAnswers", {});
  return _.map(_.get(value, "elements", []), element => {
    const id = _.get(element, "id");
    if (_.has(dragAndDropAnswers, id)) {
      return { ...element, correctElements: _.get(dragAndDropAnswers, id) };
    } else {
      return element;
    }
  });
};

/**
 *
 * @param {*} data
 * @returns Extracting out property which are not to save
 */
export const extractCorrectElementsFromData = (mode, data) => {
  const dragAndDropAnswers = {};

  const elements = _.map(data, item => {
    const itemObject = { ...item };

    // * remove draggable & isCorrect property
    _.unset(itemObject, "draggable");
    _.unset(itemObject, "isCorrect");
    _.unset(itemObject, "isPlaceholder");

    if (isDropzone(_.get(item, "category")) && mode === "create") {
      // * for dropzone, checking & filtering out deleted correct elements if any
      const correctElements = _.filter(
        _.get(itemObject, "correctElements"),
        elementId => {
          const element = _.find(data, { id: elementId });
          return isDraggable(_.get(element, "category"));
        }
      );

      // * Extracting dragAndDropAnswers for each dropzone
      if (!_.isEmpty(correctElements)) {
        _.set(dragAndDropAnswers, _.get(item, "id"), correctElements);
      }
    }

    // * remove correct elements
    _.unset(itemObject, "correctElements");

    return itemObject;
  });

  return { elements, dragAndDropAnswers };
};
